
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "../../components/ui/card"
import {
    Tabs,
    TabsContent,
} from "../../components/ui/tabs"
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from "../../components/ui/breadcrumb"
import { Overview } from "../../components/dashboard/overview"
import { RecentSales } from "../../components/dashboard/recent-sales"
import { SolProgress } from "../../components/dashboard/sol-progress"
import React, { useEffect, useState } from 'react';
import { Sidebar } from "../includes/sidebar"
import { FileText, Users, Settings, Activity, CreditCard, DollarSign, UserPlus, Clock, AlertTriangle, TrendingUp, UserCheck } from "lucide-react"
import { Topbar } from "../includes/topbar"
import { Button } from "../../components/ui/button"
import { cn } from "../../lib/utils"
import ButtonGroup from "../../components/ui/button-group"
import { adminApi, DashboardStatsResponse, KPI } from "../../api/admin.api"
import { transactionsApi, WithdrawalRequestModel } from "../../api/transactions.api"
import { toast } from "../../hooks/use-toast"
import { useTranslation } from "react-i18next"
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts'

export default function DashboardPage() {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = React.useState("global");
    const [isLoading, setIsLoading] = useState(true);
    const [stats, setStats] = useState<DashboardStatsResponse | null>(null);
    const [period, setPeriod] = useState<'today' | 'week' | 'month' | 'year' | 'all'>('all');
    const [withdrawalPending, setWithdrawalPending] = useState<WithdrawalRequestModel[]>([]);
    const [withdrawalLoading, setWithdrawalLoading] = useState(false);
    const [activityData, setActivityData] = useState<{day: string; transactions: number; retrait: number}[]>([]);

    // Fetch dashboard stats
    useEffect(() => {
        const fetchStats = async () => {
            setIsLoading(true);
            try {
                const data = await adminApi.getDashboardStats(period);
                setStats(data);
            } catch (error) {
                console.error("Failed to fetch dashboard stats", error);
                toast({
                    title: t('dashboard:kyc.errorTitle'),
                    description: t('dashboard:adminDashboard.statsError', 'Impossible de charger les statistiques'),
                    type: "error"
                });
            } finally {
                setIsLoading(false);
            }
        };

        fetchStats();
    }, [period, t]);

    // Fetch withdrawal pending + aktivite 7 jou
    useEffect(() => {
        const fetchExtra = async () => {
            setWithdrawalLoading(true);
            try {
                const wrRes = await transactionsApi.getWithdrawalRequests({ status: 'pending' });
                setWithdrawalPending((wrRes as any).requests ?? (wrRes as any).results ?? []);

                if (stats?.overview?.monthly_data?.length) {
                    const last7 = stats.overview.monthly_data.slice(-7).map((d: any) => ({
                        day: d.name || d.month || '',
                        transactions: d.total || d.transactions || 0,
                        retrait: d.retraits || 0,
                    }));
                    setActivityData(last7);
                }
            } catch {
                // Silans — pa kritik
            } finally {
                setWithdrawalLoading(false);
            }
        };
        fetchExtra();
    }, [stats]);

    // KPI Card Component
    const KPICard = ({ title, icon: Icon, value, growth, prefix = "" }: { title: string, icon: any, value: string | number, growth: number, prefix?: string }) => (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{title}</CardTitle>
                <Icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
                <div className="text-2xl font-bold">{prefix}{typeof value === 'number' ? value.toLocaleString() : value}</div>
                <p className="text-xs text-muted-foreground">
                    {t('dashboard:adminDashboard.stats.growthVsPrevious', { growth: (growth > 0 ? '+' : '') + growth })}
                </p>
            </CardContent>
        </Card>
    );

    return (
        <div className="flex h-screen overflow-hidden">
            <Sidebar />
            <div className="flex-1 flex flex-col overflow-hidden">
                <Topbar />
                <div className="flex-1 overflow-y-auto space-y-4 p-8 pt-6">
                    <div className="flex items-center justify-between space-y-2">
                        <Breadcrumb>
                            <BreadcrumbList>
                                <BreadcrumbItem>
                                    <BreadcrumbLink className="capitalize">{t('dashboard:sidebar.sections.general')}</BreadcrumbLink>
                                </BreadcrumbItem>
                                <BreadcrumbSeparator />
                                <BreadcrumbItem>
                                    <BreadcrumbLink href="/dashboard">{t('dashboard:sidebar.items.dashboard')}</BreadcrumbLink>
                                </BreadcrumbItem>
                                <BreadcrumbSeparator />
                                <BreadcrumbItem>
                                    <BreadcrumbPage className="capitalize">{t(`dashboard:adminDashboard.tabs.${activeTab}`, activeTab)}</BreadcrumbPage>
                                </BreadcrumbItem>
                            </BreadcrumbList>
                        </Breadcrumb>

                        <div className="flex items-center space-x-2">
                            <select
                                className="h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                value={period}
                                onChange={(e) => setPeriod(e.target.value as any)}
                            >
                                <option value="today">{t('dashboard:adminDashboard.periods.today')}</option>
                                <option value="week">{t('dashboard:adminDashboard.periods.week')}</option>
                                <option value="month">{t('dashboard:adminDashboard.periods.month')}</option>
                                <option value="year">{t('dashboard:adminDashboard.periods.year')}</option>
                                <option value="all">{t('dashboard:adminDashboard.periods.all', 'Tout le temps')}</option>
                            </select>
                        </div>
                    </div>

                    <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
                        <ButtonGroup>
                            <Button variant="outline" size="sm" onClick={() => setActiveTab("global")} className={cn(activeTab === "global" && "bg-muted text-foreground")}>
                                {t('dashboard:adminDashboard.tabs.global')}
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => setActiveTab("analytics")} className={cn(activeTab === "analytics" && "bg-muted text-foreground")}>
                                {t('dashboard:adminDashboard.tabs.analytics')}
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => setActiveTab("reports")} className={cn(activeTab === "reports" && "bg-muted text-foreground")}>
                                {t('dashboard:adminDashboard.tabs.reports')}
                            </Button>
                        </ButtonGroup>

                        <TabsContent value="global" className="space-y-4">
                            {isLoading ? (
                                /* Loading Skeleton */
                                <div className="space-y-4 animate-pulse">
                                    {/* KPI Cards Skeleton */}
                                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                                        {[1, 2, 3, 4].map((i) => (
                                            <div key={i} className="rounded-lg border bg-card p-6 space-y-3">
                                                <div className="flex justify-between items-center">
                                                    <div className="h-4 bg-muted rounded w-24"></div>
                                                    <div className="h-4 w-4 bg-muted rounded"></div>
                                                </div>
                                                <div className="h-8 bg-muted rounded w-32"></div>
                                                <div className="h-3 bg-muted rounded w-40"></div>
                                            </div>
                                        ))}
                                    </div>
                                    {/* Charts Skeleton */}
                                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
                                        <div className="col-span-4 rounded-lg border bg-card p-6 space-y-4">
                                            <div className="space-y-2">
                                                <div className="h-5 bg-muted rounded w-32"></div>
                                                <div className="h-4 bg-muted rounded w-48"></div>
                                            </div>
                                            <div className="h-[350px] bg-muted rounded"></div>
                                        </div>
                                        <div className="col-span-3 rounded-lg border bg-card p-6 space-y-4">
                                            <div className="space-y-2">
                                                <div className="h-5 bg-muted rounded w-40"></div>
                                                <div className="h-4 bg-muted rounded w-32"></div>
                                            </div>
                                            <div className="space-y-4">
                                                {[1, 2, 3, 4, 5].map((i) => (
                                                    <div key={i} className="flex items-center gap-4">
                                                        <div className="h-10 w-10 bg-muted rounded-full"></div>
                                                        <div className="flex-1 space-y-2">
                                                            <div className="h-4 bg-muted rounded w-3/4"></div>
                                                            <div className="h-3 bg-muted rounded w-1/2"></div>
                                                        </div>
                                                        <div className="h-4 bg-muted rounded w-16"></div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                                        <KPICard
                                            title={t('dashboard:adminDashboard.stats.totalDepots')}
                                            icon={DollarSign}
                                            value={stats?.kpis?.total_depots?.value || 0}
                                            growth={stats?.kpis?.total_depots?.growth || 0}
                                            prefix="G "
                                        />
                                        <KPICard
                                            title={t('dashboard:adminDashboard.stats.newAccounts')}
                                            icon={UserPlus}
                                            value={stats?.kpis?.nouveaux_comptes?.value || 0}
                                            growth={stats?.kpis?.nouveaux_comptes?.growth || 0}
                                        />
                                        <KPICard
                                            title={t('dashboard:adminDashboard.stats.transactions')}
                                            icon={CreditCard}
                                            value={stats?.kpis?.total_transactions?.value || 0}
                                            growth={stats?.kpis?.total_transactions?.growth || 0}
                                        />
                                        <KPICard
                                            title={t('dashboard:adminDashboard.stats.activeAccounts')}
                                            icon={Activity}
                                            value={stats?.kpis?.comptes_actifs?.value || 0}
                                            growth={stats?.kpis?.comptes_actifs?.growth || 0}
                                        />
                                    </div>

                                    {/* Charts & Lists */}
                                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
                                        <Card className="col-span-4">
                                            <CardHeader>
                                                <CardTitle>{t('dashboard:adminDashboard.charts.monthlyTransactions')}</CardTitle>
                                                <CardDescription>
                                                    {t('dashboard:adminDashboard.charts.monthlyTransactionsDesc')}
                                                </CardDescription>
                                            </CardHeader>
                                            <CardContent className="pl-2">
                                                <Overview data={stats?.overview?.monthly_data || []} />
                                            </CardContent>
                                        </Card>
                                        <Card className="col-span-3">
                                            <CardHeader>
                                                <CardTitle>{t('dashboard:adminDashboard.charts.recentTransactions')}</CardTitle>
                                                <CardDescription>
                                                    {t('dashboard:adminDashboard.charts.recentTransactionsDesc')}
                                                </CardDescription>
                                            </CardHeader>
                                            <CardContent>
                                                <RecentSales data={(stats?.recent_transactions || []).slice(0, 5)} />
                                            </CardContent>
                                        </Card>
                                    </div>

                                    {/* SOL Progress Tracking */}
                                    <div className="grid gap-4 md:grid-cols-1">
                                        <Card>
                                            <CardHeader>
                                                <CardTitle>{t('dashboard:adminDashboard.charts.solProgress')}</CardTitle>
                                                <CardDescription>
                                                    {t('dashboard:adminDashboard.charts.solProgressDesc')}
                                                </CardDescription>
                                            </CardHeader>
                                            <CardContent>
                                                <SolProgress data={stats?.sol_progress || []} />
                                            </CardContent>
                                        </Card>
                                    </div>

                                    {/* ═══ FAZ 4A: WITHDRAWAL PENDING WIDGET ═══ */}
                                    {(withdrawalLoading || withdrawalPending.length > 0) && (
                                        <div
                                            className="flex items-start gap-4 rounded-lg border border-yellow-200 bg-yellow-50 p-4 cursor-pointer hover:bg-yellow-100 transition-colors"
                                            onClick={() => window.location.href = '/transactions/retrait-requests'}
                                        >
                                            <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 shrink-0" />
                                            <div className="flex-1">
                                                <p className="text-sm font-semibold text-yellow-800">
                                                    {withdrawalPending.length} Demand{withdrawalPending.length > 1 ? 's' : ''} Retrait An Atant
                                                </p>
                                                <p className="text-xs text-yellow-700 mt-0.5">
                                                    Total:{' '}
                                                    {new Intl.NumberFormat('fr-HT', { style: 'currency', currency: 'HTG' }).format(
                                                        withdrawalPending.reduce((s, r) => s + Number(r.montant), 0)
                                                    )}
                                                    {' '}— Klike pou jere yo
                                                </p>
                                            </div>
                                            <span className="text-xs font-bold bg-yellow-200 text-yellow-800 px-2 py-1 rounded-full">
                                                {withdrawalPending.length}
                                            </span>
                                        </div>
                                    )}

                                    {/* ═══ FAZ 4A: STATISTIK AJAN GLOBAL ═══ */}
                                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                                        <Card>
                                            <CardHeader className="pb-2">
                                                <CardTitle className="text-sm font-medium flex items-center gap-2">
                                                    <UserCheck className="h-4 w-4 text-blue-500" />
                                                    Ajan Aktif
                                                </CardTitle>
                                            </CardHeader>
                                            <CardContent>
                                                <div className="text-2xl font-bold">{stats?.summary?.agents?.active || 0}</div>
                                                <p className="text-xs text-muted-foreground">
                                                    sou {stats?.summary?.agents?.total || 0} ajan antou
                                                </p>
                                                <div className="mt-3 h-1.5 w-full rounded-full bg-slate-100">
                                                    <div
                                                        className="h-1.5 rounded-full bg-blue-500"
                                                        style={{
                                                            width: `${stats?.summary?.agents?.total
                                                                ? Math.round((stats.summary.agents.active / stats.summary.agents.total) * 100)
                                                                : 0}%`
                                                        }}
                                                    />
                                                </div>
                                            </CardContent>
                                        </Card>

                                        <Card>
                                            <CardHeader className="pb-2">
                                                <CardTitle className="text-sm font-medium flex items-center gap-2">
                                                    <UserCheck className="h-4 w-4 text-green-500" />
                                                    Ajan ak Kliyan
                                                </CardTitle>
                                            </CardHeader>
                                            <CardContent>
                                                <div className="text-2xl font-bold">{stats?.summary?.agents?.with_clients || 0}</div>
                                                <p className="text-xs text-muted-foreground">ajan ki gen omwen 1 kliyan asiye</p>
                                                <div className="mt-3 h-1.5 w-full rounded-full bg-slate-100">
                                                    <div
                                                        className="h-1.5 rounded-full bg-green-500"
                                                        style={{
                                                            width: `${stats?.summary?.agents?.active
                                                                ? Math.round(((stats.summary.agents.with_clients || 0) / stats.summary.agents.active) * 100)
                                                                : 0}%`
                                                        }}
                                                    />
                                                </div>
                                            </CardContent>
                                        </Card>

                                        <Card>
                                            <CardHeader className="pb-2">
                                                <CardTitle className="text-sm font-medium flex items-center gap-2">
                                                    <Clock className="h-4 w-4 text-orange-500" />
                                                    Demand Retrait
                                                </CardTitle>
                                            </CardHeader>
                                            <CardContent>
                                                <div className="text-2xl font-bold text-orange-600">{withdrawalPending.length}</div>
                                                <p className="text-xs text-muted-foreground">demand pending kounye a</p>
                                                <button
                                                    onClick={() => window.location.href = '/transactions/retrait-requests'}
                                                    className="mt-3 text-xs text-orange-600 hover:underline font-medium"
                                                >
                                                    Wè tout demand →
                                                </button>
                                            </CardContent>
                                        </Card>
                                    </div>

                                    {/* ═══ FAZ 4A: GRAFIK AKTIVITE 7 JOU ═══ */}
                                    {activityData.length > 0 && (
                                        <Card>
                                            <CardHeader>
                                                <CardTitle className="flex items-center gap-2">
                                                    <TrendingUp className="h-4 w-4" />
                                                    Aktivite Resan
                                                </CardTitle>
                                                <CardDescription>
                                                    Tranzaksyon + Retrait pending — dènye peryòd
                                                </CardDescription>
                                            </CardHeader>
                                            <CardContent>
                                                <ResponsiveContainer width="100%" height={180}>
                                                    <BarChart data={activityData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                                                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                                        <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                                                        <YAxis tick={{ fontSize: 11 }} />
                                                        <Tooltip
                                                            formatter={(value: any, name: string) => [
                                                                value,
                                                                name === 'transactions' ? 'Tranzaksyon' : 'Retrait'
                                                            ]}
                                                        />
                                                        <Bar dataKey="transactions" fill="#3b82f6" radius={[3, 3, 0, 0]} name="transactions" />
                                                        <Bar dataKey="retrait" fill="#f59e0b" radius={[3, 3, 0, 0]} name="retrait" />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </CardContent>
                                        </Card>
                                    )}
                                </>
                            )}
                        </TabsContent>

                        <TabsContent value="analytics" className="space-y-4">
                            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                                <Card>
                                    <CardHeader>
                                        <CardTitle>{t('dashboard:adminDashboard.analytics.clients.title')}</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-2xl font-bold">{stats?.summary?.clients?.total || 0}</div>
                                        <p className="text-xs text-muted-foreground">{t('dashboard:adminDashboard.analytics.clients.total')}</p>
                                        <div className="mt-4 space-y-2">
                                            <div className="flex justify-between text-sm">
                                                <span>{t('dashboard:adminDashboard.analytics.clients.verified')}</span>
                                                <span className="font-medium">{stats?.summary?.clients?.verified || 0}</span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span>{t('dashboard:adminDashboard.analytics.clients.pending')}</span>
                                                <span className="font-medium text-orange-500">{stats?.summary?.clients?.pending_kyc || 0}</span>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardHeader>
                                        <CardTitle>{t('dashboard:adminDashboard.analytics.comptes.title')}</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-2xl font-bold">{stats?.summary?.comptes?.total || 0}</div>
                                        <p className="text-xs text-muted-foreground">{t('dashboard:adminDashboard.analytics.comptes.total')}</p>
                                        <div className="mt-4 space-y-2">
                                            <div className="flex justify-between text-sm">
                                                <span>{t('dashboard:adminDashboard.analytics.comptes.bs')}</span>
                                                <span className="font-medium">{stats?.summary?.comptes?.bs || 0}</span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span>{t('dashboard:adminDashboard.analytics.comptes.sol')}</span>
                                                <span className="font-medium">{stats?.summary?.comptes?.sol || 0}</span>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardHeader>
                                        <CardTitle>{t('dashboard:adminDashboard.analytics.agents.title')}</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-2xl font-bold">{stats?.summary?.agents?.total || 0}</div>
                                        <p className="text-xs text-muted-foreground">{t('dashboard:adminDashboard.analytics.agents.total')}</p>
                                        <div className="mt-4 space-y-2">
                                            <div className="flex justify-between text-sm">
                                                <span>{t('dashboard:adminDashboard.analytics.agents.active')}</span>
                                                <span className="font-medium">{stats?.summary?.agents?.active || 0}</span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span>{t('dashboard:adminDashboard.analytics.agents.withClients')}</span>
                                                <span className="font-medium">{stats?.summary?.agents?.with_clients || 0}</span>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        </TabsContent>

                        {/* Other tabs remain as placeholders or can be implemented later */}
                        <TabsContent value="reports">
                            <Card>
                                <CardHeader>
                                    <CardTitle>{t('dashboard:adminDashboard.tabs.reports')}</CardTitle>
                                    <CardDescription>{t('common:messages.comingSoon', 'Section en cours de développement.')}</CardDescription>
                                </CardHeader>
                            </Card>
                        </TabsContent>
                    </Tabs>
                </div>
            </div>
        </div>
    )
}
